import type { ReactNode } from "react"
import InputField from "../InputField/InputField"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { ToggleComponet } from "../Toggle/ToggleComponet"
import Animate, { AnimationTypes } from "../Animate/Animate"

export type QuickEditValue = string | number | boolean

export interface QuickEditOption {
  label: string
  value: string
}

export interface QuickEditColumn<T> {
  id: string
  title: string
  descriptionKey: string
  type: "text" | "number" | "money" | "boolean" | "select"
  group: "input" | "select" | "boolean"
  minWidthClass?: string
  getValue: (row: T) => QuickEditValue
  setValue: (row: T, value: QuickEditValue) => T
  options?: QuickEditOption[]
  validate?: (value: QuickEditValue, row: T) => string | undefined
  isDisabled?: (row: T) => boolean
  disabledMessage?: string
  bulkEditor?: ReactNode
}

interface QuickEditGridProps<T> {
  rows: T[]
  columns: QuickEditColumn<T>[]
  selectedIds: Set<number>
  getRowId: (row: T) => number
  onRowsChange: (rows: T[]) => void
  onSelectionChange: (ids: Set<number>) => void
  selectAllLabel: string
  bulkSelectionLabel: string
  emptyMessage: string
  booleanTrueLabel: string
  booleanFalseLabel: string
  selectAllDescriptionKey: string
  selectRowDescriptionKey: string
}

interface QuickEditBooleanSelectProps {
  value?: boolean
  trueLabel: string
  falseLabel: string
  disabled?: boolean
  descriptionKey: string
  onChange: (value: boolean) => void
}

export function QuickEditBooleanSelect({
  value,
  trueLabel,
  falseLabel,
  disabled,
  descriptionKey,
  onChange,
}: QuickEditBooleanSelectProps) {
  const selectedType = value === undefined ? [] : [value ? trueLabel : falseLabel]

  return (
    <div className="flex items-center justify-center">
      <ToggleComponet
        canSelectType={true}
        valuesToggle={[trueLabel, falseLabel]}
        selectedType={selectedType}
        disable={disabled}
        animationDuration="0.2s"
        descriptionKey={descriptionKey}
        handleToggleSelection={(_isOnlySelection, data, handleChangeType) => handleChangeType([data])}
        handleChangeType={(values) => {
          const selectedValue = values[0]

          if (selectedValue === trueLabel) onChange(true)
          else if (selectedValue === falseLabel) onChange(false)
        }}
      />
    </div>
  )
}

export function QuickEditGrid<T>({
  rows,
  columns,
  selectedIds,
  getRowId,
  onRowsChange,
  onSelectionChange,
  selectAllLabel,
  bulkSelectionLabel,
  emptyMessage,
  booleanTrueLabel,
  booleanFalseLabel,
  selectAllDescriptionKey,
  selectRowDescriptionKey,
}: QuickEditGridProps<T>) {
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(getRowId(row)))

  const updateCell = (rowId: number, column: QuickEditColumn<T>, value: QuickEditValue) => {
    onRowsChange(
      rows.map((row) => (getRowId(row) === rowId && !column.isDisabled?.(row) ? column.setValue(row, value) : row)),
    )
  }

  const toggleAll = () => {
    const next = new Set(selectedIds)

    rows.forEach((row) => {
      const rowId = getRowId(row)

      if (allSelected) next.delete(rowId)
      else next.add(rowId)
    })

    onSelectionChange(next)
  }

  const toggleRow = (rowId: number) => {
    const next = new Set(selectedIds)

    if (next.has(rowId)) next.delete(rowId)
    else next.add(rowId)

    onSelectionChange(next)
  }

  if (rows.length === 0) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s">
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-center text-gray-500">
          {emptyMessage}
        </div>
      </Animate>
    )
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="0.4s">
      <div className="h-0 min-h-0 min-w-0 max-w-full flex-1 overflow-auto overscroll-contain rounded-2xl border border-gray-200 bg-white shadow-sm [scrollbar-color:#fb923c_#fff7ed] [scrollbar-gutter:stable] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar-corner]:bg-orange-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-orange-50 [&::-webkit-scrollbar-thumb]:bg-orange-400 hover:[&::-webkit-scrollbar-thumb]:bg-orange-500 [&::-webkit-scrollbar-track]:bg-orange-50 [&::-webkit-scrollbar]:h-[11px] [&::-webkit-scrollbar]:w-[11px]">
        <table className="w-max min-w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky top-0 z-[3] min-w-[88px] border-b border-orange-200 bg-orange-50 p-3 text-center text-xs font-bold text-orange-900">
                <label className="flex cursor-pointer flex-col items-center gap-1">
                  <input
                    type="checkbox"
                    className="size-4 cursor-pointer accent-orange-500"
                    checked={allSelected}
                    onChange={toggleAll}
                    data-interaction-description-key={selectAllDescriptionKey}
                  />
                  <span>{selectAllLabel}</span>
                </label>
              </th>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`sticky top-0 z-[3] border-b border-orange-200 bg-orange-50 p-3 text-xs font-bold text-orange-900 ${column.group === "boolean" ? "text-center" : "text-left"} ${column.minWidthClass ?? "min-w-[150px]"}`}
                >
                  {column.title}
                </th>
              ))}
            </tr>
            <tr>
              <th className="sticky top-[45px] z-[2] min-w-[88px] border-b border-orange-300 bg-amber-50 p-2 text-center text-xs font-semibold text-orange-900">
                {bulkSelectionLabel}
              </th>
              {columns.map((column) => (
                <th
                  key={`${column.id}-bulk`}
                  className={`sticky top-[45px] z-[2] border-b border-orange-300 bg-amber-50 p-2 align-middle ${column.group === "boolean" ? "text-center" : "text-left"} ${column.minWidthClass ?? "min-w-[150px]"}`}
                >
                  {column.bulkEditor ?? <span className="font-normal text-gray-400">—</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const rowId = getRowId(row)
              const isSelected = selectedIds.has(rowId)

              return (
                <Animate
                  key={rowId}
                  type={AnimationTypes.fadeInTraslate}
                  duration="0.35s"
                  delay={`${Math.min(rowIndex, 10) * 0.035}s`}
                >
                  <tr className={`transition-colors hover:bg-orange-50/60 ${isSelected ? "bg-orange-50" : "bg-white"}`}>
                    <td className="min-w-[88px] border-b border-gray-100 bg-inherit p-2 text-center align-middle">
                      <input
                        type="checkbox"
                        className="size-4 cursor-pointer accent-orange-500"
                        checked={isSelected}
                        onChange={() => toggleRow(rowId)}
                        data-interaction-description-key={selectRowDescriptionKey}
                      />
                    </td>
                    {columns.map((column) => {
                      const value = column.getValue(row)
                      const isDisabled = column.isDisabled?.(row) ?? false
                      const validationMessage = isDisabled ? undefined : column.validate?.(value, row)

                      return (
                        <td
                          key={column.id}
                          className={`border-b border-gray-100 p-2 align-top ${column.group === "boolean" ? "text-center align-middle" : ""} ${validationMessage ? "bg-red-50" : ""}`}
                        >
                          {isDisabled ? (
                            <div className="flex min-h-11 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 text-center text-xs font-medium text-gray-400">
                              {column.disabledMessage ?? "—"}
                            </div>
                          ) : column.type === "boolean" ? (
                            <QuickEditBooleanSelect
                              value={Boolean(value)}
                              trueLabel={booleanTrueLabel}
                              falseLabel={booleanFalseLabel}
                              descriptionKey={column.descriptionKey}
                              onChange={(nextValue) => updateCell(rowId, column, nextValue)}
                            />
                          ) : column.type === "select" ? (
                            <GenericDropDown
                              data={column.options ?? []}
                              initialState={String(value)}
                              descriptionKey={column.descriptionKey}
                              handlerFuncion={(event) => updateCell(rowId, column, event.target.value)}
                            />
                          ) : (
                            <InputField
                              type={column.type === "text" ? "text" : "number"}
                              value={String(value)}
                              placeholder={column.title}
                              inputMode={column.type === "money" ? "decimal" : "numeric"}
                              min={column.type === "money" ? 0.01 : 0}
                              step={column.type === "money" ? 0.01 : 1}
                              errorMessage={validationMessage}
                              descriptionKey={column.descriptionKey}
                              onChange={(event) =>
                                updateCell(
                                  rowId,
                                  column,
                                  column.type === "text" ? event.target.value : Number(event.target.value),
                                )
                              }
                            />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                </Animate>
              )
            })}
          </tbody>
        </table>
      </div>
    </Animate>
  )
}
